import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';

/**
 * Google Review Audit — shared by the settings card, the bulk research run and
 * the contact's Intelligence view.
 *
 * Twin of the option keys: functions/helpers/reviewAuditPrompt.js (REVIEW_FOCUS_KEYS).
 */

export const GOOGLE_REVIEW_SETTINGS_PATH = (userId) => `/World/${userId}/settings/googleReviewSettings`;

export const REVIEW_FOCUS_OPTIONS = [
  { id: 'complaints', label: 'Top complaints', icon: 'fa-triangle-exclamation', hint: 'Most frequent complaint reasons with evidence' },
  { id: 'weaknesses', label: 'Weak spots', icon: 'fa-wrench', hint: 'Recurring problems the business could fix' },
  { id: 'strengths', label: 'Praised strengths', icon: 'fa-thumbs-up', hint: 'What customers highlight again and again' },
  { id: 'ownerResponse', label: 'Owner replies', icon: 'fa-reply', hint: 'Does the owner answer, and how' },
  { id: 'customerProfile', label: 'Customer profile', icon: 'fa-users', hint: 'Who writes the reviews' },
  { id: 'salesAngle', label: 'Sales angle', icon: 'fa-bullseye', hint: 'Opening angle and conversation starters' },
];

export const DEFAULT_REVIEW_FOCUS = ['complaints', 'weaknesses', 'strengths', 'ownerResponse', 'salesAngle'];

export const REVIEW_DEPTH_OPTIONS = [
  { id: 'deep', label: 'Deep', hint: 'Up to ~30 reviews: relevant, newest, worst, best' },
  { id: 'quick', label: 'Quick', hint: '~10 reviews: relevant and worst' },
];

export const REVIEW_LANGUAGE_OPTIONS = [
  { id: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { id: 'en', label: 'English', flag: '🇬🇧' },
];

export const CUSTOM_FOCUS_PRESETS = [
  { label: 'Waiting times', text: 'How are waiting times and appointment booking rated?' },
  { label: 'Phone & reachability', text: 'How do customers rate reachability by phone and email?' },
  { label: 'Price / value', text: 'What do customers say about prices and value for money?' },
  { label: 'Staff & service', text: 'How are friendliness and competence of the staff described?' },
  { label: 'Online / digital', text: 'Any mentions of website, online booking, online shop or digital processes?' },
];

export const MAX_CUSTOM_FOCUS = 400;
export const MAX_OFFER_CONTEXT = 300;

export function normalizeReviewSettings(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const known = REVIEW_FOCUS_OPTIONS.map((o) => o.id);
  const focus = Array.isArray(src.focus) ? src.focus.filter((k) => known.includes(k)) : DEFAULT_REVIEW_FOCUS;
  return {
    focus: focus.length ? focus : DEFAULT_REVIEW_FOCUS,
    depth: src.depth === 'quick' ? 'quick' : 'deep',
    language: src.language === 'en' ? 'en' : 'de',
    customFocus: String(src.customFocus || '').slice(0, MAX_CUSTOM_FOCUS),
    offerContext: String(src.offerContext || '').slice(0, MAX_OFFER_CONTEXT),
  };
}

export const REVIEW_AUDIT_FAILURES = {
  place_not_found: 'Not found on Google Maps',
  place_unavailable: 'Google Maps did not return the place',
  rate_limited: 'Google is throttling requests — try again in a few minutes',
  busy: 'Review Audit is busy — try again shortly',
  no_review_text: 'No written reviews yet',
  analysis_failed: 'Reviews loaded, AI analysis failed',
};

/**
 * Bulk pacing. Each audit already spaces its own Google requests ~1s apart on
 * the server; on top of that the bulk run keeps two audits in flight, waits a
 * few random seconds per worker between contacts and backs off when throttled.
 */
export const REVIEW_AUDIT_CONCURRENCY = 2;
export const MAX_THROTTLE_STRIKES = 3;
export const MAX_BUSY_RETRIES = 15;

export function reviewAuditGapMs(rand = Math.random()) {
  return Math.round(2500 + rand * 2500);
}

/** How long the whole run pauses after a throttled audit (strike = consecutive Google blocks). */
export function reviewAuditBackoffMs({ reason, retryAfterSec, strike = 1 }) {
  if (reason === 'busy') return (retryAfterSec || 20) * 1000;
  const fallback = Math.min(120000 * 2 ** (Math.max(strike, 1) - 1), 15 * 60 * 1000);
  return Math.max((Number(retryAfterSec) || 0) * 1000, fallback);
}

export function isReviewAuditEligible(contact) {
  return !!(contact?.business_name?.trim() || contact?.lead_website?.trim());
}

export function reviewAuditInput(contact = {}) {
  return {
    businessName: contact.business_name || '',
    website: contact.lead_website || '',
    city: contact.lead_city || '',
    address: [contact.lead_street, contact.lead_postcode, contact.lead_city].filter(Boolean).join(' '),
    country: contact.lead_country || '',
  };
}

/** Calls the Cloud Function. Throws on transport/auth errors, returns `{ ok:false, reason }` for misses. */
export async function requestGoogleReviewAudit({ userId, contact }) {
  const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.googleReviewAudit(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      worldId: userId,
      contact: reviewAuditInput(contact),
      featureId: contact?.google_place_feature_id || null,
    }),
  });
  // 429 from Cloud Functions itself = all instances busy (maxInstances), not Google.
  if (res.status === 429) return { ok: false, reason: 'busy', retryAfterSec: 20 };
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.message || json.error || `HTTP ${res.status}`);
  return json;
}

/**
 * Fields written to the contact. `audit` lives under researchIntelligence so
 * the Intelligence view, mail generation and the Company Record can read it.
 */
export function buildReviewAuditUpdate(result) {
  const audit = {
    place: result.place || null,
    stats: result.stats || null,
    topics: result.topics || [],
    reviews: result.reviews || [],
    analysis: result.analysis || null,
    settings: result.settings || null,
    auditedAt: result.auditedAt || new Date().toISOString(),
  };
  const firestore = {
    'researchIntelligence.googleReviewAudit': audit,
    google_place_feature_id: result.place?.featureId || null,
  };
  const rowPatch = { google_place_feature_id: firestore.google_place_feature_id };
  if (typeof result.place?.rating === 'number') {
    firestore.lead_rating = result.place.rating;
    rowPatch.lead_rating = result.place.rating;
  }
  if (typeof result.place?.reviewCount === 'number') {
    firestore.lead_reviews = result.place.reviewCount;
    rowPatch.lead_reviews = result.place.reviewCount;
  }
  return { audit, firestore, rowPatch };
}
